'use client';

import type { InternalNetworkDto } from '@terra/shared-contracts-internal-network';
import {
  InternalNetworkExecutionSettingsContent,
  toasterApi,
  type InternalNetworkExecutionPersistRequest,
} from '@terra/ui-design-system';
import { useTranslations } from 'next-intl';
import { useUpdateInternalNetwork } from '../../../../hooks/api/useSurfaceSettings';

type InternalExecutionSettingsPanelProps = {
  surface: InternalNetworkDto;
  networkId: string;
  isReadOnly: boolean;
  isDiscoveryActive: boolean;
};

export function InternalExecutionSettingsPanel({
  surface,
  networkId,
  isReadOnly,
  isDiscoveryActive,
}: InternalExecutionSettingsPanelProps) {
  const tSettings = useTranslations('internalNetwork.settings');
  const updateSurface = useUpdateInternalNetwork(networkId);

  const persistExecutionSettings = async ({
    updates,
    showSuccessToast,
  }: InternalNetworkExecutionPersistRequest): Promise<boolean> => {
    if (isReadOnly) {
      return false;
    }

    try {
      await updateSurface.mutateAsync(updates);

      if (showSuccessToast) {
        toasterApi.success({ title: tSettings('saveSuccess') });
      }

      return true;
    } catch (error: unknown) {
      console.error(
        new Error('Failed to update internal network execution settings'),
        error,
      );
      toasterApi.error({ title: tSettings('saveError') });

      return false;
    }
  };

  return (
    <InternalNetworkExecutionSettingsContent
      network={surface}
      isReadOnly={isReadOnly}
      isDiscoveryActive={isDiscoveryActive}
      isUpdating={updateSurface.isPending}
      onPersist={persistExecutionSettings}
    />
  );
}
